import type { ReactNode } from 'react';
import { Card, Col, ProgressBar, Row } from 'react-bootstrap';

import { KPI_NA, isKpiMissingDisplay } from '../Bias/ModuleKpiDashboard';
import '../Bias/BiasAggregateSummaryPanel.css';
import {
  LLM_CONTROL_KEYS,
  LLM_CONTROL_LABELS,
  ML_CONTROL_KEYS,
  ML_CONTROL_LABELS,
  type AISafetyInputType,
  type AISafetyRun,
  controlScore,
  readControls,
  resolveControlEntry,
  resolveInputType
} from './aiSafety.utils';

const ROW_ACCENTS = ['violet', 'fuchsia', 'rose', 'emerald'] as const;

function fmtScore3(v: unknown): string {
  if (v == null || v === '') return KPI_NA;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return KPI_NA;
  return n.toFixed(3);
}

function fmtText(v: unknown): string {
  if (v == null || v === '') return KPI_NA;
  const s = String(v).trim();
  return s.length ? s : KPI_NA;
}

function datasetVerdictBadgeBg(raw: unknown): 'danger' | 'warning' | 'success' | 'secondary' {
  const u = String(raw ?? '')
    .trim()
    .toUpperCase();
  if (u === 'REJECT') return 'danger';
  if (u === 'REVIEW') return 'warning';
  if (u === 'APPROVE') return 'success';
  return 'secondary';
}

function riskTierHeroTone(riskTierLabel: string | null): 'high' | 'medium' | 'low' | 'unknown' {
  const r = (riskTierLabel ?? '').toUpperCase();
  if (r === 'HIGH') return 'high';
  if (r === 'MEDIUM' || r === 'MODERATE') return 'medium';
  if (r === 'LOW') return 'low';
  return 'unknown';
}

function riskTierProgressVariant(
  tone: 'high' | 'medium' | 'low' | 'unknown'
): 'danger' | 'warning' | 'success' | 'secondary' {
  if (tone === 'high') return 'danger';
  if (tone === 'medium') return 'warning';
  if (tone === 'low') return 'success';
  return 'secondary';
}

function KpiTile({ label, value, accent }: { label: string; value: ReactNode; accent: string }) {
  return (
    <div className="bias-dash-tile bias-dash-tile--compact h-100" data-accent={accent}>
      <div className="bias-dash-tile__label">{label}</div>
      <div className="bias-dash-tile__value">{value}</div>
    </div>
  );
}

function buildMlMetrics(run: AISafetyRun): { label: string; score: string }[] {
  const controls = readControls(run);
  return ML_CONTROL_KEYS.slice(0, 4).map(key => ({
    label: ML_CONTROL_LABELS[key],
    score: fmtScore3(controlScore(controls[`ml_${key}`]))
  }));
}

function buildLlmMetrics(run: AISafetyRun): { label: string; score: string }[] {
  const controls = readControls(run);
  return LLM_CONTROL_KEYS.map(key => ({
    label: LLM_CONTROL_LABELS[key],
    score: fmtScore3(controlScore(resolveControlEntry(controls, key, 'llm')))
  }));
}

function buildBottomMetrics(run: AISafetyRun, inputType: AISafetyInputType): { label: string; score: string }[] {
  const controls = readControls(run);

  if (inputType === 'ML_APP' || (inputType === 'BOTH' && Object.keys(controls).some(k => k.startsWith('ml_')))) {
    return buildMlMetrics(run);
  }

  return buildLlmMetrics(run);
}

function buildHybridMetrics(run: AISafetyRun): { label: string; score: string }[] {
  const controls = readControls(run);
  const rows: { label: string; score: string }[] = [];
  for (const key of LLM_CONTROL_KEYS) {
    rows.push({
      label: `LLM ${LLM_CONTROL_LABELS[key]}`,
      score: fmtScore3(controlScore(resolveControlEntry(controls, key, 'llm')))
    });
  }
  for (const key of ML_CONTROL_KEYS.slice(0, 4)) {
    rows.push({
      label: `ML ${ML_CONTROL_LABELS[key]}`,
      score: fmtScore3(controlScore(controls[`ml_${key}`]))
    });
  }
  return rows;
}

function resolveVerdictCardAccent(verdictBarVariant: 'danger' | 'warning' | 'success' | 'secondary'): string {
  if (verdictBarVariant === 'danger') return 'bias-dash-verdict-card--danger';
  if (verdictBarVariant === 'warning') return 'bias-dash-verdict-card--warning';
  if (verdictBarVariant === 'success') return 'bias-dash-verdict-card--success';
  return '';
}

function resolveBottomMetrics(
  run: AISafetyRun,
  lane: 'ml' | 'llm' | undefined,
  inputType: AISafetyInputType
): { label: string; score: string }[] {
  if (lane === 'ml') return buildMlMetrics(run);
  if (lane === 'llm') return buildLlmMetrics(run);
  if (inputType === 'BOTH') return buildHybridMetrics(run);
  return buildBottomMetrics(run, inputType);
}

export function AISafetyDashboard({ run, lane }: { run: AISafetyRun | null | undefined; lane?: 'ml' | 'llm' }) {
  if (!run) {
    return (
      <div className="text-muted small py-3" aria-label="AI Safety module KPIs">
        No AI Safety evaluation data available for this run.
      </div>
    );
  }

  const inputType = resolveInputType(run);
  const riskScoreRaw = run.risk_score ?? run.Risk_Score;
  const riskScore = fmtScore3(riskScoreRaw);
  const riskScoreNum = Number(riskScoreRaw);
  const riskScoreBarNow =
    isKpiMissingDisplay(riskScore) || !Number.isFinite(riskScoreNum) || riskScoreNum < 0
      ? 0
      : Math.min(100, Math.max(0, riskScoreNum));

  const riskTierRaw = fmtText(run.risk_tier ?? run.Risk_Tier);
  const tierTone = riskTierHeroTone(isKpiMissingDisplay(riskTierRaw) ? null : riskTierRaw);
  const tierVariant = riskTierProgressVariant(tierTone);

  const datasetStatus = fmtText(run.review_status ?? run.Review_Status ?? run.dataset_status);
  const datasetVerdictRaw = fmtText(run.dataset_verdict ?? run.Dataset_Verdict);
  const verdictBarVariant = datasetVerdictBadgeBg(run.dataset_verdict ?? run.Dataset_Verdict);
  const verdictCardAccent = resolveVerdictCardAccent(verdictBarVariant);

  const riskScoreBarVariant =
    isKpiMissingDisplay(riskScore) || !Number.isFinite(riskScoreNum) || riskScoreNum < 0 ? 'secondary' : tierVariant;

  const bottomMetrics = resolveBottomMetrics(run, lane, inputType);
  const displayMetrics = bottomMetrics.slice(0, lane || inputType !== 'BOTH' ? 4 : 8);

  return (
    <div className="bias-dash mb-2" aria-label="AI Safety module KPIs">
      <div className="bias-dash-verdict-cards">
        <Row className="g-2 g-md-2 mt-1 align-items-stretch">
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card
              className={`bias-dash-verdict-card bias-dash-verdict-card--accent bias-dash-risk-tier-card h-100 flex-fill w-100 border-0 shadow-sm bias-dash-risk-tier-card--${tierTone}`}
            >
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Risk Tier</span>
                <div
                  className={`bias-dash-verdict-card__value text-uppercase bias-dash-risk-tier-card__value--${tierTone}`}
                >
                  {riskTierRaw}
                </div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Categorical severity of the safety evaluation.
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(riskTierRaw) ? 0 : 100}
                    variant={tierVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card className="bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm">
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Risk Score</span>
                <div className="bias-dash-verdict-card__value">{riskScore}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Safety risk on a 0–100 scale (3&nbsp;decimal display).
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={riskScoreBarNow}
                    variant={riskScoreBarVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card className="bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm">
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Dataset Status</span>
                <div className="bias-dash-verdict-card__value text-break">{datasetStatus}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Current processing state of the dataset.
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(datasetStatus) ? 0 : 100}
                    variant="secondary"
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card
              className={`bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm ${verdictCardAccent}`}
            >
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Dataset Verdict</span>
                <div className="bias-dash-verdict-card__value text-uppercase">{datasetVerdictRaw}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Governance gate decision (APPROVE / REVIEW / REJECT).
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(datasetVerdictRaw) ? 0 : 100}
                    variant={verdictBarVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
      <Row className="g-2 mt-2">
        {displayMetrics.map((k, i) => (
          <Col key={k.label} xs={12} sm={6} md={inputType === 'BOTH' ? 3 : 3}>
            <KpiTile label={k.label} value={k.score} accent={ROW_ACCENTS[i % ROW_ACCENTS.length]} />
          </Col>
        ))}
      </Row>
      {inputType === 'BOTH' && !lane ? (
        <div className="small text-muted mt-1">Hybrid project — LLM and ML safety controls shown.</div>
      ) : null}
    </div>
  );
}

export default AISafetyDashboard;
